import { useEffect, useRef, useState } from 'react';

import { isAxiosError } from 'axios';

import { client } from '../api/generated/client.gen';
import { AuthContext } from './AuthContext';
import { TOKEN_MIN_VALIDITY, getKeycloak, keycloakInitOptions } from './keycloak';
import { pickDefaultTenantId, useTenantStore } from './tenantStore';
import type { AuthState, MeResponse } from './types';

async function hydrateTenantStore(): Promise<'ok' | 'forbidden' | 'error'> {
  try {
    const res = await client.instance.get<MeResponse>('/me');
    const me = res.data;
    const store = useTenantStore.getState();
    store.setMe(me);

    const next = pickDefaultTenantId(me, store.currentTenantId);
    // setCurrentTenant clears the query cache; only call it when the id
    // actually changes to avoid blowing away the cache on every page load.
    if (next !== store.currentTenantId) {
      store.setCurrentTenant(next);
    }
    return 'ok';
  } catch (err) {
    if (isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403)) {
      return 'forbidden';
    }
    return 'error';
  }
}

let keycloakInitialized = false;
let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;

function extractUserInfo() {
  const tp = getKeycloak().tokenParsed;
  return {
    id: tp?.sub || '',
    username: tp?.preferred_username || '',
    email: tp?.email || '',
    firstName: tp?.given_name,
    lastName: tp?.family_name,
    roles: tp?.realm_access?.roles || [],
  };
}

function setupTokenRefresh() {
  if (tokenRefreshInterval) clearInterval(tokenRefreshInterval);
  const keycloak = getKeycloak();

  tokenRefreshInterval = setInterval(async () => {
    try {
      await keycloak.updateToken(TOKEN_MIN_VALIDITY);
    } catch {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }
    }
  }, 60000);

  keycloak.onTokenExpired = () => {
    keycloak.updateToken(5).catch(console.error);
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    initialized: false,
    authenticated: false,
    user: null,
    loading: true,
    error: null,
    accessDenied: false,
  });

  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      const keycloak = getKeycloak();
      try {
        if (keycloakInitialized) {
          if (keycloak.authenticated && keycloak.token) {
            const meStatus = await hydrateTenantStore();
            setState({
              initialized: true,
              authenticated: true,
              user: extractUserInfo(),
              loading: false,
              error: null,
              accessDenied: meStatus === 'forbidden',
            });
            setupTokenRefresh();
          } else {
            await keycloak.login({ redirectUri: window.location.origin });
          }
          return;
        }

        const authenticated = await keycloak.init(keycloakInitOptions);
        keycloakInitialized = true;

        if (authenticated && keycloak.token) {
          const meStatus = await hydrateTenantStore();
          setState({
            initialized: true,
            authenticated: true,
            user: extractUserInfo(),
            loading: false,
            error: null,
            accessDenied: meStatus === 'forbidden',
          });
          setupTokenRefresh();
        } else {
          setState({
            initialized: true,
            authenticated: false,
            user: null,
            loading: false,
            error: 'Authentication failed',
            accessDenied: false,
          });
        }
      } catch (err) {
        setState({
          initialized: true,
          authenticated: false,
          user: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to initialize authentication',
          accessDenied: false,
        });
      }
    };

    init();
  }, []);

  const login = async () => {
    await getKeycloak().login({ redirectUri: window.location.origin });
  };

  const logout = async () => {
    if (tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval);
      tokenRefreshInterval = null;
    }
    useTenantStore.getState().reset();
    await getKeycloak().logout({ redirectUri: window.location.origin });
  };

  const getToken = () => getKeycloak().token;

  const updateToken = async (): Promise<string | undefined> => {
    const keycloak = getKeycloak();
    try {
      await keycloak.updateToken(TOKEN_MIN_VALIDITY);
      return keycloak.token;
    } catch {
      setState((prev) => ({
        ...prev,
        authenticated: false,
        user: null,
        error: 'Session expired.',
      }));
      keycloak.login({ redirectUri: window.location.origin });
      return undefined;
    }
  };

  const hasRole = (role: string) => getKeycloak().hasRealmRole(role);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, getToken, updateToken, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}
